import { useState, useEffect } from "react";
import { Trash2, Plus, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLanguages = () => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [flagEmoji, setFlagEmoji] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadLanguages(); }, []);

  const loadLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data);
    } catch (error) {
      toast.error("Failed to load languages");
    } finally {
      setLoading(false);
    }
  };

  const createLanguage = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/languages`, { name, code, flag_emoji: flagEmoji }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Language added!");
      setName(""); setCode(""); setFlagEmoji(""); setShowForm(false);
      loadLanguages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add language");
    } finally {
      setCreating(false);
    }
  };

  const deleteLanguage = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/languages/${deleteTarget}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Language deleted");
      setDeleteTarget(null);
      loadLanguages();
    } catch (error) {
      toast.error("Failed to delete language");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2" data-testid="languages-title">Languages</h1>
          <p className="text-[#4A4A4A]">Available languages for courses</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="brutalist-btn-primary flex items-center gap-2" data-testid="add-language-btn">
          <Globe className="w-5 h-5" /> Add Language
        </button>
      </div>

      {showForm && (
        <form onSubmit={createLanguage} className="brutalist-card p-6 mb-6 space-y-4" data-testid="add-language-form">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Language name (e.g., French)" className="brutalist-input" required data-testid="new-lang-name" />
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Language code (e.g., fr)" className="brutalist-input" required maxLength={5} data-testid="new-lang-code" />
          <Input value={flagEmoji} onChange={(e) => setFlagEmoji(e.target.value)} placeholder="Flag emoji (e.g., flag)" className="brutalist-input" data-testid="new-lang-flag" />
          <button type="submit" disabled={creating} className="brutalist-btn-primary flex items-center gap-2" data-testid="create-language-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Language
          </button>
        </form>
      )}

      {languages.length === 0 ? (
        <div className="brutalist-card p-12 text-center"><p className="text-[#4A4A4A]">No languages added yet.</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {languages.map(l => (
            <div key={l.id} className="brutalist-card p-6 flex items-center justify-between" data-testid={`language-card-${l.id}`}>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{l.flag_emoji}</span>
                <div>
                  <h3 className="font-bold">{l.name}</h3>
                  <span className="text-sm text-[#4A4A4A] font-mono">{l.code}</span>
                </div>
              </div>
              <button onClick={() => setDeleteTarget(l.id)} className="p-2 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded" data-testid={`delete-language-${l.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Language?"
        description="This will remove this language. Courses using it may be affected. This action cannot be undone."
        onConfirm={deleteLanguage}
        destructive
      />
    </div>
  );
};

export default AdminLanguages;
